/**
 * Frontend mirror of /app/backend/config/plans.config.py
 * -------------------------------------------------------
 * Used to drive UI gating: feature locks, upsell modal, plan badges, etc.
 *
 * IMPORTANT: keep this file in sync with the backend Python config.
 * Pricing and feature flags must match plans.config.py exactly.
 *
 * Plan resolution from auth context:
 *   - workspace.plan === 'free'        -> 'starter'
 *   - workspace.plan === 'pro'         -> 'pro'
 *   - workspace.plan === 'enterprise'  -> 'enterprise'
 *   - user.role === 'admin' (Platform) -> 'enterprise' (full access)
 */

export const PLAN_STARTER = 'starter';
export const PLAN_PRO = 'pro';
export const PLAN_ENTERPRISE = 'enterprise';

export const PLAN_ORDER = [PLAN_STARTER, PLAN_PRO, PLAN_ENTERPRISE];

export const PLANS = {
  [PLAN_STARTER]: {
    id: PLAN_STARTER,
    name: 'Starter',
    tagline: {
      fr: 'L\'essentiel pour bien demarrer',
      en: 'The essentials to get started',
    },
    price_monthly: 29,
    price_suffix: { fr: '/mois', en: '/month' },
    currency: 'CAD',
    is_free: false,
    // Explicit, translated bullet list shown on the landing page & billing UI.
    display_features: {
      fr: [
        'CRM de base',
        'Formulaires de capture',
        'Gestion simple des prospects',
        '1 utilisateur',
      ],
      en: [
        'Basic CRM',
        'Lead capture forms',
        'Simple lead management',
        '1 user',
      ],
    },
    features: {
      seo_articles: false,
      ai_visibility: false,
      gmb_module: false,
      sms_enabled: false,
      smtp_enabled: false,
      ai_agents: false,
      multi_user: false,
      custom_branding: false,
      api_access: false,
      voice_ai: false,
      video_generation: false,
    },
    limits: {
      max_users: 1, max_leads: 50, max_clients: 25,
      max_employees: 0, max_subcontractors: 0,
    },
  },
  [PLAN_PRO]: {
    id: PLAN_PRO,
    name: 'Pro',
    tagline: {
      fr: 'Pour faire decoller votre entreprise',
      en: 'To grow your business faster',
    },
    price_monthly: 97,
    price_suffix: { fr: '/mois', en: '/month' },
    currency: 'CAD',
    is_free: false,
    display_features: {
      fr: [
        '🚀 Suite IA Marketing complète',
        'AI Visibility (positionnement dans les LLMs)',
        'Articles SEO générés par IA',
        'Analyse concurrentielle IA',
        'Opportunités SEO IA',
        'Module Google My Business',
        'SMS automatisés',
        'Multi-utilisateurs',
        '15 générations IA / mois',
      ],
      en: [
        '🚀 Full AI Marketing suite',
        'AI Visibility (LLM positioning)',
        'AI-generated SEO articles',
        'AI competitor analysis',
        'AI SEO opportunities',
        'Google My Business module',
        'Automated SMS',
        'Multi-users',
        '15 AI generations / month',
      ],
    },
    features: {
      seo_articles: true,
      ai_visibility: true,
      gmb_module: true,
      sms_enabled: true,
      smtp_enabled: true,
      ai_agents: false,
      multi_user: true,
      custom_branding: false,
      api_access: false,
      voice_ai: false,
      video_generation: false,
    },
    limits: {
      max_users: 5, max_leads: 1000, max_clients: 500,
      max_employees: 10, max_subcontractors: 10,
    },
  },
  [PLAN_ENTERPRISE]: {
    id: PLAN_ENTERPRISE,
    name: 'Enterprise',
    tagline: {
      fr: 'Pour les equipes qui veulent tout, avec accompagnement',
      en: 'For teams that want it all, with hands-on support',
    },
    price_monthly: 297,
    // Trailing "+" because Enterprise pricing is "from 297$".
    price_suffix: { fr: '+ /mois', en: '+ /month' },
    currency: 'CAD',
    is_free: false,
    display_features: {
      fr: [
        '🔥 IA Marketing incluse — suite complète',
        'AI Visibility maximale',
        'SEO Intelligence avancée',
        'Analyse concurrentielle IA',
        'Opportunités SEO IA',
        'Rapports IA avancés',
        'Agents IA conversationnels',
        'IA vocale + génération vidéo',
        'Utilisateurs et sous-traitants illimités',
        '25 générations IA / mois + extras admin',
        'Marque blanche + API publique',
        'Support humain prioritaire',
      ],
      en: [
        '🔥 AI Marketing included — full suite',
        'Maximum AI Visibility',
        'Advanced SEO Intelligence',
        'AI competitor analysis',
        'AI SEO opportunities',
        'Advanced AI reports',
        'Conversational AI agents',
        'Voice AI + video generation',
        'Unlimited users & subcontractors',
        '25 AI generations / month + admin extras',
        'White-label + Public API',
        'Priority human support',
      ],
    },
    features: {
      seo_articles: true,
      ai_visibility: true,
      gmb_module: true,
      sms_enabled: true,
      smtp_enabled: true,
      ai_agents: true,
      multi_user: true,
      custom_branding: true,
      api_access: true,
      voice_ai: true,
      video_generation: true,
    },
    limits: {
      max_users: -1, max_leads: -1, max_clients: -1,
      max_employees: -1, max_subcontractors: -1,
    },
  },
};

/**
 * Resolve the effective plan id for a given (user, workspace) pair.
 *  - Platform admin (role === 'admin') always has Enterprise access for UI gating.
 *  - Otherwise we read workspace.plan and map legacy 'free' -> 'starter'.
 *  - Unknown / missing values fall back to 'starter' (safest default).
 */
export function resolvePlanId(user, workspace) {
  if (user?.role === 'admin') return PLAN_ENTERPRISE;
  const raw = workspace?.plan;
  if (raw === 'pro') return PLAN_PRO;
  if (raw === 'enterprise') return PLAN_ENTERPRISE;
  // 'free' or anything else
  return PLAN_STARTER;
}

export function getPlan(planId) {
  return PLANS[planId] || PLANS[PLAN_STARTER];
}

export function hasFeature(planId, featureKey) {
  return !!getPlan(planId).features[featureKey];
}

/**
 * Return the minimum plan id that unlocks a given feature.
 * Used by the upsell modal to suggest the right plan.
 */
export function minPlanForFeature(featureKey) {
  for (const pid of PLAN_ORDER) {
    if (hasFeature(pid, featureKey)) return pid;
  }
  return PLAN_ENTERPRISE;
}

/** Human-readable label per feature, for the upsell modal. */
export const FEATURE_LABELS = {
  seo_articles:     { fr: 'Marketing IA & SEO',          en: 'AI Marketing & SEO' },
  ai_visibility:    { fr: 'Visibilite IA',                en: 'AI Visibility' },
  gmb_module:       { fr: 'Google My Business',           en: 'Google My Business' },
  sms_enabled:      { fr: 'Envoi de SMS',                 en: 'SMS Sending' },
  smtp_enabled:     { fr: 'Emails transactionnels',       en: 'Transactional Emails' },
  ai_agents:        { fr: 'Agents IA conversationnels',   en: 'AI Conversational Agents' },
  multi_user:       { fr: 'Equipes & employes',           en: 'Teams & Employees' },
  custom_branding:  { fr: 'Marque blanche',               en: 'Custom Branding' },
  api_access:       { fr: 'Acces API publique',           en: 'Public API Access' },
  voice_ai:         { fr: 'IA vocale',                    en: 'Voice AI' },
  video_generation: { fr: 'Generation de videos',         en: 'Video Generation' },
};
